/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import {
	ISchemaFlowNodeDefault,
	ISchemaFlowNodeListModel,
	ISchemaFlowNodeModel,
	ISchemaFlowNodeTypeDefinitionMap
} from "@adapptio/lib-blueprint";
import { BEM, TTranslateHookFn, useTranslate } from "@adapptio/lib-components";
import { emitEvent } from "@adapptio/lib-shared";
import React, { FunctionComponent, useCallback, useEffect, useRef, useState } from "react";
import ReactFlow, {
	applyEdgeChanges,
	applyNodeChanges,
	Edge,
	MiniMap,
	Node,
	ReactFlowInstance,
	ReactFlowProps,
	ReactFlowProvider,
	Viewport
} from "@adapptio/vendor-react-flow-renderer";
import { useBlueprintModel } from "../../hooks/useBlueprintModel";
import { FlowEditorNode, TFlowEditorNode } from "./FlowEditorNode";
import { FLOW_EDITOR_LAYOUT, TGetNodeInfoFn } from "./FlowEditorTypes";

// Mime type used to store dragged node
export const FLOW_EDITOR_DND_MIMETYPE = "application/adpt-editor-flow-node";

/**
 * Offset to drop position (related to the CSS!)
 */
const NODE_DROP_OFFSET = {
	x: -75, // ($edt-flow-editor-node--width-layout-vertical / 2)
	y: -16 // $edt-flow-editor-node--min-height +- <empiricaly determined value>
};

// Define node types
const nodeTypes: ReactFlowProps["nodeTypes"] = {
	editorNode: FlowEditorNode
};

const snapGrid: ReactFlowProps["snapGrid"] = [ 10, 10 ];

const deleteKeyCode: ReactFlowProps["deleteKeyCode"] = [ "Delete", "Backspace" ];

function getFlowElements(
	modelNode: ISchemaFlowNodeListModel<ISchemaFlowNodeTypeDefinitionMap>,
	layoutType: FLOW_EDITOR_LAYOUT,
	getNodeInfo: TGetNodeInfoFn<ISchemaFlowNodeTypeDefinitionMap>,
	t: TTranslateHookFn,
	defaultSelectedIds: string[]
): { nodes: TFlowEditorNode[]; edges: Edge[] } {
	const nodes: TFlowEditorNode[] = [];
	const edges: Edge[] = [];

	for (let i = 0; i < modelNode.items.length; i++) {
		const item = modelNode.items[i];
		const nodeType = modelNode.schema.opts.nodeTypes[item.type];
		const outputDescriptors = nodeType.resolveOutputs(item);
		const selected = defaultSelectedIds?.includes(item.id);

		const node: TFlowEditorNode = {
			id: item.id,
			type: "editorNode",
			position: {
				x:
					layoutType === FLOW_EDITOR_LAYOUT.HORIZONTAL
						? item.position.props.x.value
						: item.position.props.y.value,
				y:
					layoutType === FLOW_EDITOR_LAYOUT.HORIZONTAL
						? item.position.props.y.value
						: item.position.props.x.value
			},
			data: {
				model: item,
				isStartNode:
					modelNode.schema.opts.entryNode && item.id === modelNode.schema.opts.entryNode.id
						? true
						: false,
				layoutType: layoutType,
				getNodeInfo: getNodeInfo
			}
		};

		if (selected) {
			node.selected = true;
		}

		// Add node
		nodes.push(node);

		// Add edges
		for (const k in item.outputs.props) {
			for (let j = 0; j < item.outputs.props[k].items.length; j++) {
				const target = item.outputs.props[k].items[j];

				const edgeClass = BEM("edt-flow-editor", "edge", {
					"was-activated": item.executionResult?.activeOutput === k
				});

				edges.push({
					id: `edge-${item.id}-${k}_${target.value}`,
					source: item.id,
					target: target.value,
					label: t("editor", outputDescriptors[k].label),
					labelShowBg: true,
					labelBgPadding: [ 4, 1 ],
					sourceHandle: k,
					style: {
						strokeWidth: 1
					},
					animated: true,
					className: edgeClass.toClassName()
				});
			}
		}
	}

	return { nodes, edges };
}

/**
 * Component props
 */
export interface IFlowEditor {
	modelNode: ISchemaFlowNodeListModel<ISchemaFlowNodeTypeDefinitionMap>;
	layoutType: FLOW_EDITOR_LAYOUT;
	canRemoveElements: (elements: { nodes?: Node[]; edges?: Edge[] }) => boolean;
	defaultTransform?: Viewport;
	showMiniMap?: boolean;
	className?: string;
	onReactFlowLoad?: ReactFlowProps["onInit"];
	onSelectionChange?: (modelNodes: Array<ISchemaFlowNodeModel<ISchemaFlowNodeTypeDefinitionMap>>) => void;
	getNodeInfo?: TGetNodeInfoFn<ISchemaFlowNodeTypeDefinitionMap>;
	defaultSelectedIds?: string[];
}

/**
 * Component to edit flow node list
 */
export const FlowEditor: FunctionComponent<IFlowEditor> = ({
	modelNode,
	layoutType,
	defaultTransform,
	showMiniMap,
	canRemoveElements: canDeleteElements,
	className,
	onReactFlowLoad,
	onSelectionChange,
	getNodeInfo,
	defaultSelectedIds
}) => {
	const t = useTranslate();
	const { model, updateRef } = useBlueprintModel(modelNode, "flowNodeList");
	const [ reactFlowInstance, setReactFlowInstance ] = useState<ReactFlowInstance>(null);
	const reactFlowWrapper = useRef(null);
	const [ isViewportInitialized, setIsViewportInitialized ] = useState(false);
	const [ isConnecting, setIsConnecting ] = useState(false);

	const [ nodes, setNodes ] = useState<TFlowEditorNode[]>([]);
	const [ edges, setEdges ] = useState<Edge[]>([]);

	const handelReactFlowLoad = useCallback<ReactFlowProps["onInit"]>((_reactFlowInstance) => {
		setReactFlowInstance(_reactFlowInstance);

		if (onReactFlowLoad) {
			onReactFlowLoad(_reactFlowInstance);
		}
	}, []);

	const updateNodePosition = useCallback<ReactFlowProps["onNodeDragStop"]>(
		(_ev, node) => {
			const itemModel = node.data.model as ISchemaFlowNodeModel<ISchemaFlowNodeTypeDefinitionMap>;
			itemModel.schema.setPosition(
				itemModel,
				layoutType === FLOW_EDITOR_LAYOUT.HORIZONTAL ? node.position.x : node.position.y,
				layoutType === FLOW_EDITOR_LAYOUT.HORIZONTAL ? node.position.y : node.position.x,
				true
			);
		},
		[ layoutType ]
	);

	const updateSelectionPosition = useCallback<ReactFlowProps["onSelectionDragStop"]>(
		(_ev, nodes) => {
			for (let i = 0; i < nodes.length; i++) {
				const itemModel = nodes[i].data
					.model as ISchemaFlowNodeModel<ISchemaFlowNodeTypeDefinitionMap>;
				itemModel.schema.setPosition(
					itemModel,
					layoutType === FLOW_EDITOR_LAYOUT.HORIZONTAL ? nodes[i].position.x : nodes[i].position.y,
					layoutType === FLOW_EDITOR_LAYOUT.HORIZONTAL ? nodes[i].position.y : nodes[i].position.x,
					true
				);
			}
		},
		[ layoutType ]
	);

	const handleConnect = useCallback<ReactFlowProps["onConnect"]>(
		(connection) => {
			if (!model) {
				return;
			}

			const srcModel = model.schema.getItemById(model, connection.source);
			const dstModel = model.schema.getItemById(model, connection.target);

			if (srcModel && dstModel) {
				srcModel.schema.connectOutput(srcModel, connection.sourceHandle, connection.target, true);
				emitEvent(model.changeEvent);
			}
		},
		[ model ]
	);

	const handleConnectStart = useCallback(() => {
		setIsConnecting(true);
	}, []);

	const handleConnectEnd = useCallback(() => {
		setIsConnecting(false);
	}, []);

	const handleDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		event.dataTransfer.dropEffect = "move";
	}, []);

	const handleDrop = useCallback(
		(event: React.DragEvent<HTMLDivElement>) => {
			event.preventDefault();

			const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
			const data = event.dataTransfer.getData(FLOW_EDITOR_DND_MIMETYPE);
			const position = reactFlowInstance.project({
				x: Math.floor((event.clientX - reactFlowBounds.left) / 10) * 10 + NODE_DROP_OFFSET.x,
				y: Math.floor((event.clientY - reactFlowBounds.top) / 10) * 10 + NODE_DROP_OFFSET.y
			});

			if (data) {
				const itemDefaults = JSON.parse(
					data
				) as ISchemaFlowNodeDefault<ISchemaFlowNodeTypeDefinitionMap>;
				itemDefaults.position = {
					x: layoutType === FLOW_EDITOR_LAYOUT.HORIZONTAL ? position.x : position.y,
					y: layoutType === FLOW_EDITOR_LAYOUT.HORIZONTAL ? position.y : position.x
				};
				model.schema.addItem(model, itemDefaults, true);
			}
		},
		[ model, reactFlowWrapper, reactFlowInstance ]
	);

	const handleSelectionChange = useCallback<ReactFlowProps["onSelectionChange"]>(
		({ nodes, edges }) => {
			if (!onSelectionChange) {
				return;
			}

			const modelNodes: Array<ISchemaFlowNodeModel<ISchemaFlowNodeTypeDefinitionMap>> = [];

			const elements = [ ...nodes, ...edges ];

			if (Array.isArray(elements)) {
				for (let i = 0; i < elements.length; i++) {
					if (elements[i]?.data?.model) {
						modelNodes.push(
							elements[i].data.model as ISchemaFlowNodeModel<ISchemaFlowNodeTypeDefinitionMap>
						);
					}
				}
			}

			onSelectionChange(modelNodes);
		},
		[ onSelectionChange ]
	);

	const onNodesDelete = useCallback<ReactFlowProps["onNodesDelete"]>(
		(nodes) => {
			if (!model || !canDeleteElements({ nodes })) {
				return;
			}

			for (let i = 0; i < nodes.length; i++) {
				const node = nodes[i];

				// Prevent delete of start node
				if (model.schema.opts.entryNode?.id && node.id === model.schema.opts.entryNode?.id) {
					return;
				}

				model.schema.removeItemById(model, node.id, true);
				emitEvent(model.changeEvent);
			}
		},
		[ model ]
	);

	const onEdgesDelete = useCallback<ReactFlowProps["onEdgesDelete"]>(
		(edges) => {
			if (!model || !canDeleteElements({ edges })) {
				return;
			}

			for (let i = 0; i < edges.length; i++) {
				const edge = edges[i];
				const srcModel = model.schema.getItemById(model, edge.source);

				if (srcModel) {
					console.log(srcModel.outputs);
					srcModel.schema.disconnectOutput(srcModel, edge.sourceHandle, edge.target, true);
					emitEvent(model.changeEvent);
				}
			}
		},
		[ model ]
	);

	const handleNodesChange = useCallback<ReactFlowProps["onNodesChange"]>((changes) => {
		const removing = changes.some((change) => change.type === "remove");

		if (removing && !canDeleteElements({})) {
			return;
		}

		setNodes((ns) => applyNodeChanges(changes, ns));
	}, []);

	const handleEdgesChange = useCallback<ReactFlowProps["onEdgesChange"]>((changes) => {
		const removing = changes.some((change) => change.type === "remove");

		if (removing && !canDeleteElements({})) {
			return;
		}

		setEdges((es) => applyEdgeChanges(changes, es));
	}, []);

	const handleNodeClick = useCallback<ReactFlowProps["onNodeClick"]>(
		(event, node) => {
			if (!onSelectionChange) {
				return;
			}

			if (node?.data?.model) {
				onSelectionChange([ node.data.model ]);
			}
		},
		[ onSelectionChange ]
	);

	const handleEdgeClick = useCallback<ReactFlowProps["onEdgeClick"]>(
		(event, edge) => {
			if (!onSelectionChange) {
				return;
			}

			if (edge?.data?.model) {
				onSelectionChange([ edge.data.model ]);
			}
		},
		[ onSelectionChange ]
	);

	const configureInitialViewport = useCallback(() => {
		if (defaultTransform) {
			reactFlowInstance.setViewport(defaultTransform);
		} else {
			reactFlowInstance.fitView({
				minZoom: 1,
				maxZoom: 1
			});

			const _state = reactFlowInstance.toObject();

			// reactFlowInstance.setCenter(0, 0)

			reactFlowInstance.setViewport({
				x: _state.viewport.x,
				y: _state.viewport.y,
				zoom: _state.viewport.zoom
			});
		}

		setIsViewportInitialized(true);
	}, [ reactFlowInstance, defaultTransform, setIsViewportInitialized ]);

	// Initialize
	useEffect(() => {
		if (model) {
			const { nodes, edges } = getFlowElements(model, layoutType, getNodeInfo, t, defaultSelectedIds);
			setNodes(nodes);
			setEdges(edges);
		} else {
			setNodes((prevNodes) => (prevNodes.length > 0 ? [] : prevNodes));
			setEdges((prevEdges) => (prevEdges.length > 0 ? [] : prevEdges));
		}
	}, [ updateRef, model, layoutType ]);

	// Re-center when visible on screen (prevents jumping effect)
	useEffect(() => {
		const element = reactFlowWrapper.current;

		if (element && reactFlowInstance) {
			const observer = new IntersectionObserver((entries) => {
				if (entries[0].isIntersecting) {
					observer.disconnect();
					configureInitialViewport();	
				}
			});
			observer.observe(element);
		}
	}, [ reactFlowWrapper, reactFlowInstance ]);

	if (!model) {
		return <p>Invalid model node.</p>;
	}

	const baseClass = BEM("edt-flow-editor", null, {
		"is-connecting": isConnecting,
		"is-ready": isViewportInitialized
	});

	if (className) {
		baseClass.add(className);
	}

	return (
		<div className={baseClass.toClassName()} ref={reactFlowWrapper}>
			<ReactFlowProvider>
				<ReactFlow
					nodes={nodes}
					edges={edges}
					nodeTypes={nodeTypes}
					snapToGrid={true}
					snapGrid={snapGrid}
					zoomOnScroll={false}
					onNodeClick={handleNodeClick}
					onEdgeClick={handleEdgeClick}
					onSelectionChange={handleSelectionChange}
					onConnect={handleConnect}
					onConnectStart={handleConnectStart}
					onConnectEnd={handleConnectEnd}
					onNodesChange={handleNodesChange}
					onNodesDelete={onNodesDelete}
					onEdgesChange={handleEdgesChange}
					onEdgesDelete={onEdgesDelete}
					onNodeDragStop={updateNodePosition}
					onSelectionDragStop={updateSelectionPosition}
					onDragOver={handleDragOver}
					onDrop={handleDrop}
					onInit={handelReactFlowLoad}
					deleteKeyCode={deleteKeyCode}
				>
					{showMiniMap ? <MiniMap /> : null}
				</ReactFlow>
			</ReactFlowProvider>
		</div>
	);
};
